import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
  Typography,
} from "@mui/material";
import { DateTime } from "luxon";
import { ReactElement, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";

import {
  ScheduleCampaignFullInitialSendMutation,
  ScheduleCampaignFullInitialSendMutationVariables,
  StartCampaignFullInitialSendMutation,
  StartCampaignFullInitialSendMutationVariables,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { RhfDateTimePicker } from "~frontend/components/forms/react-hook-form-components";
import {
  GET_CAMPAIGN_FOR_VIEW,
  SCHEDULE_CAMPAIGN_FULL_INITIAL_SEND,
  START_CAMPAIGN_FULL_INITIAL_SEND,
} from "~frontend/graphql/campaigns";
import { useSubaccountAdminConfig } from "~frontend/providers/SubaccountProvider";
import { SendSummaryCard } from "~frontend/routes/subaccounts/campaignView/initialSend/SendSummaryCard";
import { CampaignForView } from "~frontend/routes/subaccounts/campaignView/types";
import { plural } from "~frontend/util/plural";
import {
  createScheduleForSchema,
  useTextingHours,
} from "~frontend/util/textingHours";
import useMutation from "~frontend/util/useMutation";

const EQUAL_SPLIT_DISCLAIMER =
  "Note that sending across script variants will roughly split your list equally, but we can't guarantee an exact split.";

function remainingTextCounts({
  totalTextCount,
  nLeftToSend,
}: {
  totalTextCount: number;
  nLeftToSend: number;
}) {
  return (
    <>
      {totalTextCount === nLeftToSend ? "all" : "the remaining"}{" "}
      <strong>{plural(nLeftToSend, "text", "texts")}</strong>
    </>
  );
}

export function FullSendDialog({
  campaign,
  handleClose,
}: {
  campaign: CampaignForView;
  handleClose: () => void;
}): ReactElement {
  const subaccount = useSubaccountAdminConfig()!;

  const start = campaign.textingHoursStart ?? subaccount.textingHoursStart;
  const end = campaign.textingHoursEnd ?? subaccount.textingHoursEnd;
  const timezone =
    campaign.textingHoursTimezone ?? subaccount.textingHoursTimezone;

  const textingHours = { start, end, timezone };

  const { isInTextingHours } = useTextingHours(textingHours);

  const schema = useMemo(
    () =>
      z.object({
        scheduleFor: createScheduleForSchema({ start, end, timezone }),
      }),
    [start, end, timezone]
  );

  const [sendFull] = useMutation<
    StartCampaignFullInitialSendMutation,
    StartCampaignFullInitialSendMutationVariables
  >(START_CAMPAIGN_FULL_INITIAL_SEND, {
    refetchQueries: [GET_CAMPAIGN_FOR_VIEW],
    awaitRefetchQueries: true,
    onErrorSnackbarMessage: "Failed to start initial send",
  });

  const [scheduleFull] = useMutation<
    ScheduleCampaignFullInitialSendMutation,
    ScheduleCampaignFullInitialSendMutationVariables
  >(SCHEDULE_CAMPAIGN_FULL_INITIAL_SEND, {
    refetchQueries: [GET_CAMPAIGN_FOR_VIEW],
    awaitRefetchQueries: true,
    onErrorSnackbarMessage: "Failed to schedule initial send",
  });

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    reValidateMode: "onBlur",
  });

  const scheduleFor = useWatch({ control, name: "scheduleFor" });
  const isScheduled = !!scheduleFor || !isInTextingHours;

  async function onSubmit(input: { scheduleFor?: Date | null }) {
    if (input.scheduleFor) {
      await scheduleFull({
        variables: {
          input: {
            campaignId: campaign.id,
            scheduleFor: input.scheduleFor,
          },
        },
      });
    } else {
      await sendFull({
        variables: {
          input: {
            campaignId: campaign.id,
          },
        },
      });
    }

    handleClose();
  }

  const totalTextCount = campaign.quickStats?.contactCount ?? 0;
  const nLeftToSend = campaign.quickStats
    ? campaign.quickStats.contactCount - campaign.quickStats.initialSentCount
    : 0;
  const isSingleVariant =
    campaign.scriptVariants.filter(s => !s.deletedAt).length === 1;

  const now = DateTime.now();
  return (
    <Dialog open={true} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 0, mb: 0 }}>
        Send all Texts for {campaign.name}
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Typography sx={{ mb: 2 }} variant="body1">
            Confirm that the below information is correct, then click "send" to
            send {remainingTextCounts({ totalTextCount, nLeftToSend })} for your
            campaign.
          </Typography>
          <Typography sx={{ mb: 2 }}>
            Without a send time, your campaign will send immediately. If you
            enter a send time, your Campaign will run from the send time you
            select until it is complete or the end of texting hours.
          </Typography>
          {!isSingleVariant && (
            <Typography sx={{ mb: 2 }} variant="body1">
              {EQUAL_SPLIT_DISCLAIMER}
            </Typography>
          )}
          <RhfDateTimePicker
            name="scheduleFor"
            dateLabel="Send Date"
            timeLabel="Send Time"
            minTime={now}
            minDate={now}
            control={control}
            optional={isInTextingHours}
            timezone={timezone}
          />
          <SendSummaryCard campaign={campaign} />
          <DialogActions
            sx={{ justifyContent: "flex-end", pl: 0, pr: 0, pt: 2 }}
          >
            <FlexBox gap={1}>
              <Button variant="outlined" onClick={handleClose}>
                CLOSE
              </Button>
              <Button
                variant="contained"
                type="submit"
                disabled={isSubmitting}
                loading={isSubmitting}
              >
                {isScheduled ? "SCHEDULE SEND" : "SEND NOW"}
              </Button>
            </FlexBox>
          </DialogActions>
        </DialogContent>
      </form>
    </Dialog>
  );
}
